import cn from 'classnames';
import React, { memo, ReactNode, useCallback, useMemo } from 'react';

import Text from 'components/atoms/text';
import { FloatingTooltipText } from 'components/molecules/tooltipFloating';
import { FloatingTooltipPlacement } from 'components/molecules/tooltipFloating/types';
import { HIDE_FROM_FOCUS } from 'constants/global';
import { useDebouncedCallback } from 'hooks/useDebouncedCallback';

import sm from './styles.module.scss';

type ButtonView = 'Default' | 'Add_More' | 'Only_Icon';

export interface ButtonProps {
  view?: ButtonView;
  label?: string;
  onClick?: ReactCallBack;
  isDisabled?: boolean;
  isOutlined?: boolean;
  isBig?: boolean;
  isSmall?: boolean;
  isSmallLabel?: boolean;
  isHeightOfFormInput?: boolean;
  isDanger?: boolean;
  isWide?: boolean;
  isWider?: boolean;
  type?: ButtonType;
  preIcon?: ReactNode;
  postIcon?: ReactNode;
  withFullWidth?: boolean;
  withBlueLabel?: boolean;
  withPrimaryStylesOnHover?: boolean;
  withGreyBorder?: boolean;
  withSamePadding?: boolean;
  isActive?: boolean;
  withAutoFocus?: boolean;
  tooltipText?: string;
  tooltipAllowedPlacements?: FloatingTooltipPlacement[];
  classes?: string;
  backgroundColor?: string;
  borderColor?: string;
  useDebounce?: boolean;
  formId?: string;
}

export const Button = ({
  view = 'Default',
  withAutoFocus,
  label,
  type,
  onClick,
  isOutlined,
  isDanger,
  isBig,
  isSmall,
  isSmallLabel,
  isDisabled,
  isActive,
  isWide,
  preIcon,
  postIcon,
  withFullWidth,
  withBlueLabel,
  withPrimaryStylesOnHover,
  withGreyBorder,
  tooltipText,
  tooltipAllowedPlacements,
  classes,
  withSamePadding,
  backgroundColor,
  borderColor,
  isHeightOfFormInput,
  useDebounce,
  isWider,
  formId,
}: ButtonProps) => {
  const renderIcon: (classNames: string, icon: ReactNode) => JSX.Element =
    useCallback(
      (classNames: string, icon) => <div className={classNames}>{icon}</div>,
      []
    );

  const debouncedClick = useDebouncedCallback(onClick, 300);

  const btnJsx = (
    <button
      {...(isDisabled && { tabIndex: HIDE_FROM_FOCUS })}
      type={onClick && !type ? 'button' : type}
      disabled={isDisabled}
      form={formId}
      autoFocus={withAutoFocus}
      className={useMemo(
        () =>
          cn([
            sm[view],
            {
              [sm.Outlined]: isOutlined,
              [sm.WithGreyBorder]: isOutlined && withGreyBorder,
              [sm.Danger]: isDanger && !isDisabled,
              [sm[`${view}_Disabled`]]: isDisabled,
              [sm[`${view}_isBig`]]: isBig,
              [sm[`${view}_WithSamePadding`]]: withSamePadding,
              [sm.IsSmall]: isSmall,
              [sm.IsWide]: isWide,
              [sm.IsWider]: isWider,
              [sm.OutlinedDisabled]: isDisabled && isOutlined,
              [sm.Icon]: preIcon || postIcon,
              [sm.FullWidth]: withFullWidth,
              [sm.WithPrimaryHover]: withPrimaryStylesOnHover,
              [sm.PrimaryPressed]: isActive,
              [sm.isHeightOfFormInput]: isHeightOfFormInput,
            },
            classes,
          ]),
        [
          view,
          isOutlined,
          withGreyBorder,
          isDanger,
          isDisabled,
          isBig,
          withSamePadding,
          isSmall,
          isWide,
          isWider,
          preIcon,
          postIcon,
          withFullWidth,
          withPrimaryStylesOnHover,
          isActive,
          isHeightOfFormInput,
          classes,
        ]
      )}
      style={{
        backgroundColor,
        borderColor,
      }}
      onClick={useDebounce ? debouncedClick : onClick}
    >
      {!!preIcon &&
        renderIcon(
          cn(sm.Icon_Wrapper, sm.IconLeft, {
            [sm.IconLeft_WithoutPadding]: !label,
          }),
          preIcon
        )}
      {!!label && (
        <Text
          content={label}
          withGreyColor={isDisabled && isOutlined}
          withWhiteColor={!isOutlined}
          with12Font={isSmallLabel}
          with17Font={isBig && !isSmallLabel}
          withSemiBoldWeight={!isDisabled && !isSmallLabel}
          withBlueColor={withBlueLabel}
          noWrap
        />
      )}
      {!!postIcon &&
        renderIcon(
          cn(sm.Icon_Wrapper, sm.IconRight, {
            [sm.IconRight_WithoutPadding]: !label,
          }),
          postIcon
        )}
    </button>
  );

  if (tooltipText)
    return (
      <FloatingTooltipText
        title={tooltipText ?? ''}
        useDefaultWidthAndHeight
        allowedPlacements={tooltipAllowedPlacements}
      >
        {btnJsx}
      </FloatingTooltipText>
    );

  return btnJsx;
};

export default memo(Button);
